<template>
  <div>

    <!-- LOADING -->
    <div :class="(loading ? 'show-loader' : 'hide-loader')">
      <div class="el-loading-spinner">
        <svg viewBox="25 25 50 50" class="circular">
          <circle cx="50" cy="50" r="20" fill="none" class="path"></circle>
        </svg>
      </div>
    </div>

    <!-- FORM -->
    <form class="row pad-to-15" @submit.prevent="saveStocks">

        <!-- Buton Salvare Manuală -->
        <div class="save-wrapper" v-if="products.length > 0">
          <button 
            type="submit" 
            class="btn btn-primary btn-fill"
            :disabled="saving"
          >
            <i class="ti-save"></i> 
            {{ saving ? $t('stock.saving') : $t('stock.saveStocks') }}
          </button>
        </div>

      <div class="col-md-12" v-if="errors.items.length">
        <div class="alert alert-danger alert-custom-danger">{{$t('validations.checkErrors')}}</div>
      </div>

      <!-- TABEL STOCK PRODUSE -->
      <div class="col-md-12">
        <div class="card pricing-table-card">
          <div class="card-header">
            <h4>{{ $t('stock.title') }}</h4>
            <small class="text-muted">{{ $t('stock.subtitle') }}</small>
          </div>
          <div class="card-content">

            <!-- FILTRE -->
            <div class="filters-wrapper">
              <div class="row">
                <div class="col-md-4">
                  <div class="form-group">
                    <label>{{ $t('stock.searchByName') }}</label>
                    <input 
                      type="text" 
                      class="form-control"
                      v-model="filters.name"
                      :placeholder="$t('stock.productNamePlaceholder')"
                      @input="debouncedLoadProducts"
                    >
                  </div>
                </div>
                <div class="col-md-3">
                  <div class="form-group">
                    <label>{{ $t('stock.searchById') }}</label>
                    <input 
                      type="number" 
                      class="form-control"
                      v-model="filters.product_id"
                      :placeholder="$t('stock.productIdPlaceholder')"
                      @input="debouncedLoadProducts"
                    >
                  </div>
                </div>
                <div class="col-md-3">
                  <div class="form-group">
                    <label>{{$t('catalogProd.category')}}</label>
                    <select 
                      class="form-control"
                      v-model="filters.category_id"
                      @change="loadProducts"
                    >
                      <option value="">{{ $t('stock.allCategories') }}</option>
                      <option 
                        v-for="option in mainCategories"
                        :value="option.id"
                        :key="option.id"
                      >
                        {{ option.name }}
                      </option>
                    </select>
                  </div>
                </div>
                <div class="col-md-2">
                  <div class="form-group">
                    <label>&nbsp;</label>
                    <button 
                      type="button" 
                      class="btn btn-default btn-block"
                      @click="resetFilters"
                    >
                      <i class="ti-close"></i> {{ $t('stock.resetFilters') }}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <!-- Loading pentru produse -->
            <div v-if="loadingProducts" class="text-center py-3">
              <i class="ti-reload rotate"></i> {{ $t('stock.loadingProducts') }}
            </div>

            <div v-else class="table-responsive-wrapper">
              <div class="table-horizontal-scroll">
                <table class="table pricing-table">
                  <thead>
                    <tr>
                      <!-- Coloana Produs (sticky) -->
                      <th class="product-col sticky-col">{{ $t('stock.product') }}</th>

                      <!-- Câte o coloană per locație -->
                      <th 
                        v-for="location in locations" 
                        :key="location.id"
                        class="location-col"
                      >
                        {{ location.name }}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="product in products" :key="product.id">
                      <!-- Info Produs (sticky) -->
                      <td class="product-col sticky-col">
                        <div class="product-info">
                          <strong @click="goToProduct(product)">{{ product.name }}</strong>
                          <div class="product-id text-muted">{{ $t('stock.idLabel', { id: product.id }) }}</div>
                          <div class="standard-price text-muted">
                            {{ product.price }} {{ $t('stock.currency') }}
                          </div>
                        </div>
                      </td>

                      <!-- Celulă stock per locație -->
                      <td 
                        v-for="location in locations" 
                        :key="`${product.id}-${location.true_id}`"
                        class="location-cell"
                        :class="{ 
                          'not-available': !isProductInLocation(product, location.true_id),
                          'inactive': !getStockData(product.id, location.true_id).active && isProductInLocation(product, location.true_id)
                        }"
                      >
                        <!-- Produs nu e în locație -->
                        <template v-if="!isProductInLocation(product, location.true_id)">
                          <div class="not-available-text">
                            <i class="ti-close"></i>
                            <span>{{ $t('stock.notAvailable') }}</span>
                          </div>
                        </template>

                        <!-- Produs în locație -->
                        <template v-else>
                          <!-- Activ / Inactiv -->
                          <div class="field-row active-row">
                            <label class="checkbox-label active-label">
                              <input 
                                type="checkbox" 
                                v-model="getStockData(product.id, location.true_id).active"
                                @change="toggleActive(product.id, location.true_id)"
                              >
                              <span>{{ $t('stock.active') }}</span>
                            </label>
                          </div>

                          <!-- Câmpuri (disabled când inactiv) -->
                          <div :class="{ 'disabled-fields': !getStockData(product.id, location.true_id).active }">
                            <!-- Stock (disabled dacă sync_gestiune) -->
                            <div class="field-row">
                              <label>{{ $t('stock.stock') }}</label>
                              <input 
                                type="number" 
                                step="0.01"
                                min="0"
                                class="form-control input-sm"
                                :class="{ 'disabled-input': getStockData(product.id, location.true_id).sync_gestiune }"
                                v-model="getStockData(product.id, location.true_id).stock"
                                @change="markDirty(product.id, location.true_id)"
                                :placeholder="$t('stock.stockPlaceholder')"
                                :disabled="!getStockData(product.id, location.true_id).active || getStockData(product.id, location.true_id).sync_gestiune"
                              >
                            </div>

                            <!-- Rezervare (bucati) - ascuns când allowNegative e true -->
                            <div class="field-row" v-if="!getStockData(product.id, location.true_id).allow_negative_stock">
                              <label>{{ $t('stock.reservation') }}</label>
                              <input 
                                type="number" 
                                step="1"
                                min="0"
                                class="form-control input-sm"
                                :class="{ 'input-error': isReservationInvalid(product.id, location.true_id) }"
                                v-model="getStockData(product.id, location.true_id).rezervare_stock"
                                @change="onReservationChange(product.id, location.true_id)"
                                :placeholder="$t('stock.reservationPlaceholder')"
                                :disabled="!getStockData(product.id, location.true_id).active"
                              >
                              <small v-if="isReservationInvalid(product.id, location.true_id)" class="text-danger">
                                {{ $t('stock.reservationError') }}
                              </small>
                            </div>

                            <!-- Sync Gestiune -->
                            <!-- <div class="field-row checkbox-row">
                              <label class="checkbox-label">
                                <input 
                                  type="checkbox" 
                                  v-model="getStockData(product.id, location.true_id).sync_gestiune"
                                  @change="onSyncGestiuneChange(product.id, location.true_id)"
                                  :disabled="!getStockData(product.id, location.true_id).active"
                                >
                                <span>Sync Gestiune</span>
                              </label>
                            </div>
 -->
                            <!-- Decrease Stock (informativ, disabled dacă sync_gestiune) -->
                            <!-- <div class="field-row checkbox-row">
                              <label class="checkbox-label" :class="{ 'disabled-label': getStockData(product.id, location.true_id).sync_gestiune }">
                                <input 
                                  type="checkbox" 
                                  v-model="getStockData(product.id, location.true_id).decrease_stock"
                                  @change="markDirty(product.id, location.true_id)"
                                  :disabled="!getStockData(product.id, location.true_id).active || getStockData(product.id, location.true_id).sync_gestiune"
                                >
                                <span>Scade Stock</span>
                              </label>
                            </div> -->

                            <!-- Allow Negative (disabled dacă sync_gestiune) -->
                            <div class="field-row checkbox-row">
                              <label class="checkbox-label" :class="{ 'disabled-label': getStockData(product.id, location.true_id).sync_gestiune }">
                                <input 
                                  type="checkbox" 
                                  v-model="getStockData(product.id, location.true_id).allow_negative_stock"
                                  @change="onAllowNegativeChange(product.id, location.true_id)"
                                  :disabled="!getStockData(product.id, location.true_id).active || getStockData(product.id, location.true_id).sync_gestiune"
                                >
                                <span>{{ $t('stock.allowNegative') }}</span>
                              </label>
                            </div>

                            <br>
                            <div class="row">
                              <div class="col-md-12">
                                <span style="font-size: 0.7em;">{{ $t('stock.activateForSources') }}</span>
                              </div>
                              <div class="col-md-6">
                                <!-- Active POS -->
                                <div class="field-row checkbox-row">
                                  <label class="checkbox-label">
                                    <input 
                                      type="checkbox" 
                                      v-model="getStockData(product.id, location.true_id).active_pos"
                                      @change="markDirty(product.id, location.true_id)"
                                      :disabled="!getStockData(product.id, location.true_id).active"
                                    >
                                    <span>{{ $t('stock.pos') }}</span>
                                  </label>
                                </div>

                                <!-- Active App -->
                                <div class="field-row checkbox-row">
                                  <label class="checkbox-label">
                                    <input 
                                      type="checkbox" 
                                      v-model="getStockData(product.id, location.true_id).active_app"
                                      @change="markDirty(product.id, location.true_id)"
                                      :disabled="!getStockData(product.id, location.true_id).active"
                                    >
                                    <span>{{ $t('stock.app') }}</span>
                                  </label>
                                </div>

                                <!-- Active Website -->
                                <div class="field-row checkbox-row">
                                  <label class="checkbox-label">
                                    <input 
                                      type="checkbox" 
                                      v-model="getStockData(product.id, location.true_id).active_website"
                                      @change="markDirty(product.id, location.true_id)"
                                      :disabled="!getStockData(product.id, location.true_id).active"
                                    >
                                    <span>{{ $t('stock.website') }}</span>
                                  </label>
                                </div>
                              </div>
                              <div class="col-md-6">
                                <!-- Active QR -->
                                <div class="field-row checkbox-row">
                                  <label class="checkbox-label">
                                    <input 
                                      type="checkbox" 
                                      v-model="getStockData(product.id, location.true_id).active_qr"
                                      @change="markDirty(product.id, location.true_id)"
                                      :disabled="!getStockData(product.id, location.true_id).active"
                                    >
                                    <span>{{ $t('stock.qr') }}</span>
                                  </label>
                                </div>

                                <!-- Active Kiosk -->
                                <div class="field-row checkbox-row">
                                  <label class="checkbox-label">
                                    <input 
                                      type="checkbox" 
                                      v-model="getStockData(product.id, location.true_id).active_kiosk"
                                      @change="markDirty(product.id, location.true_id)"
                                      :disabled="!getStockData(product.id, location.true_id).active"
                                    >
                                    <span>{{ $t('stock.kiosk') }}</span>
                                  </label>
                                </div>

                              </div>
                            </div>

                          </div>
                        </template>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <!-- Paginare -->
            <div class="pagination-wrapper" v-if="productsPagination.totalPages > 1">
              <div class="pagination-info">
                {{ $t('stock.page', { page: productsPagination.page, totalPages: productsPagination.totalPages }) }}
                {{ $t('stock.productsCount', { totalRecords: productsPagination.totalRecords }) }}
              </div>
              <div class="pagination-controls">
                <button 
                  class="btn btn-sm btn-default" 
                  :disabled="productsPagination.page === 1 || saving"
                  @click.prevent="changePage(productsPagination.page - 1)"
                >
                  <i class="ti-arrow-left"></i> {{ $t('stock.previous') }}
                </button>
                <button 
                  class="btn btn-sm btn-default" 
                  :disabled="productsPagination.page === productsPagination.totalPages || saving"
                  @click.prevent="changePage(productsPagination.page + 1)"
                >
                  {{ $t('stock.next') }} <i class="ti-arrow-right"></i>
                </button>
              </div>
            </div>

            <!-- Empty state -->
            <div v-if="!loadingProducts && products.length === 0" class="text-center py-4 text-muted">
              <i class="ti-package" style="font-size: 48px;"></i>
              <p>{{ $t('stock.noProductsAvailable') }}</p>
            </div>

          </div>
        </div>
      </div>
    </form>
  </div>
</template>

<script>
/* eslint-disable */
import Vue from 'vue'
import swal from 'sweetalert2'
import 'sweetalert2/dist/sweetalert2.css'

export default {
  props: {},
  data () {
    return {
      loading: true,
      loadingProducts: false,
      saving: false,
      companyId: this.$session.get('selectedCompany'),
      token: this.$session.get('token'),

      filters: {
        name: '',
        product_id: '',
        category_id: ''
      },
      debounceTimer: null,
      mainCategories: [],
      products: [],
      locations: [],
      productsPagination: {
        page: 1,
        perPage: 20,
        totalPages: 0,
        totalRecords: 0
      },
      stockData: {}
    }
  },
  created () {
    this.getCategories()
    this.loadLocations()
    this.loadProducts()
  },
  beforeDestroy () {
    this.autoSave()
  },
  methods: {

    loadLocations () {
      let companyData = this.$session.get('selectedCompanyData')
      this.locations = companyData.locations_details.map(l => ({
        id: l.id,
        true_id: l.true_id,
        name: l.name
      }))
    },

    isProductInLocation (product, locationTrueId) {
      if (!product.location_ids || product.location_ids.length === 0) {
        return true
      }
      return product.location_ids.includes(locationTrueId)
    },

    getStockData (productId, locationTrueId) {
      const key = `${productId}_${locationTrueId}`
      if (!this.stockData[key]) {
        Vue.set(this.stockData, key, {
          product_id: productId,
          location_id: locationTrueId,
          active: false,
          stock_id: null,
          _original_stock_id: null,
          stock: 0,
          rezervare_stock: 0,
          sync_gestiune: false,
          decrease_stock: true,
          allow_negative_stock: true,
          active_app: true,
          active_website: true,
          active_qr: true,
          active_kiosk: true,
          active_pos: true,
          _deleted: false,
          _dirty: false,
        })
      }
      return this.stockData[key]
    },

    markDirty (productId, locationTrueId) {
      const key = `${productId}_${locationTrueId}`
      const stock = this.stockData[key]
      if (stock) {
        Vue.set(stock, '_dirty', true)
      }
    },

    toggleActive (productId, locationTrueId) {
      const key = `${productId}_${locationTrueId}`
      const stock = this.stockData[key]
      if (!stock) return

      const isActive = stock.active
      Vue.set(stock, '_dirty', true)

      if (!isActive) {
        Vue.set(stock, '_deleted', true)
      } else {
        Vue.set(stock, '_deleted', false)
        if (!stock.stock_id) {
          Vue.set(stock, 'stock', 0)
          Vue.set(stock, 'rezervare_stock', 0)
          Vue.set(stock, 'sync_gestiune', false)
          Vue.set(stock, 'decrease_stock', true)
          Vue.set(stock, 'allow_negative_stock', true)
          Vue.set(stock, 'active_app', true)
          Vue.set(stock, 'active_website', true)
          Vue.set(stock, 'active_qr', true)
          Vue.set(stock, 'active_kiosk', true)
          Vue.set(stock, 'active_pos', true)
        }
      }
    },

    onSyncGestiuneChange (productId, locationTrueId) {
      const key = `${productId}_${locationTrueId}`
      const stock = this.stockData[key]
      if (!stock || !stock.active) return

      Vue.set(stock, '_dirty', true)

      if (stock.sync_gestiune) {
        Vue.set(stock, 'decrease_stock', false)
      }
    },

    onAllowNegativeChange (productId, locationTrueId) {
      const key = `${productId}_${locationTrueId}`
      const stock = this.stockData[key]
      if (!stock || !stock.active) return

      Vue.set(stock, '_dirty', true)

      // Dacă allow_negative devine true, resetăm rezervarea la 0
      if (stock.allow_negative_stock) {
        Vue.set(stock, 'rezervare_stock', 0)
      }
    },

    isReservationInvalid (productId, locationTrueId) {
      const key = `${productId}_${locationTrueId}`
      const stock = this.stockData[key]
      if (!stock) return false
      if (stock.allow_negative_stock) return false

      const rezervare = parseFloat(stock.rezervare_stock) || 0
      const stoc = parseFloat(stock.stock) || 0
      return rezervare > stoc
    },

    onReservationChange (productId, locationTrueId) {
      const key = `${productId}_${locationTrueId}`
      const stock = this.stockData[key]
      if (!stock) return

      // Dacă allowNegative e false, validăm că rezervarea nu depășește stockul
      if (!stock.allow_negative_stock) {
        const rezervare = parseFloat(stock.rezervare_stock) || 0
        const stoc = parseFloat(stock.stock) || 0
        if (rezervare > stoc) {
          Vue.set(stock, 'rezervare_stock', stoc)
        }
      }

      Vue.set(stock, '_dirty', true)
    },

    getCategories () {
      const data = {
        token: this.$session.get('token'),
        company_id: this.$session.get('selectedCompany')
      }
      this.apiRequest('/category', 'GET', data, (response) => {
        if (response.status === true) {
          this.mainCategories = response.data
        }
      })
    },

    debouncedLoadProducts () {
      clearTimeout(this.debounceTimer)
      this.debounceTimer = setTimeout(() => {
        this.productsPagination.page = 1 
        this.loadProducts()
      }, 500)
    },

    resetFilters () {
      this.filters = {
        name: '',
        product_id: '',
        category_id: ''
      }
      this.productsPagination.page = 1
      this.loadProducts()
    },

    async loadProducts () {
      await this.autoSave()

      this.loadingProducts = true

      const data = {
        token: this.token,
        company_id: this.companyId,
        page: this.productsPagination.page,
        perPage: this.productsPagination.perPage
      }

      if (this.filters.name) data.name = this.filters.name
      if (this.filters.product_id) data.product_id = this.filters.product_id
      if (this.filters.category_id) data.category_id = this.filters.category_id

      this.apiRequest('/product-stocks/products', 'GET', data, (response) => {
        if (response.status) {
          this.products = response.data
          this.productsPagination = response.pagination || this.productsPagination
          this.initializeStockData(response.data)
        }
        this.loadingProducts = false
        this.loading = false
      })
    },

    initializeStockData (products) {
      products.forEach(product => {
        this.locations.forEach(location => {
          const key = `${product.id}_${location.true_id}`

          const existingStock = product.stocks && product.stocks[location.true_id] 
            ? product.stocks[location.true_id] 
            : null

          const isActive = !!existingStock

          Vue.set(this.stockData, key, {
            product_id: product.id,
            location_id: location.true_id,
            active: isActive,
            stock_id: existingStock ? existingStock.id : null,
            _original_stock_id: existingStock ? existingStock.id : null,
            stock: existingStock ? existingStock.stock : 0,
            rezervare_stock: existingStock ? existingStock.rezervare_stock : 0,
            sync_gestiune: existingStock ? (existingStock.sync_inventory ? true : false) : false,
            decrease_stock: existingStock ? (existingStock.decrease_stock ? true : false) : true,
            allow_negative_stock: existingStock ? (existingStock.allow_negative_stock ? true : false) : true,
            active_app: existingStock ? (existingStock.active_app || existingStock.active_app === 1 ? true : false) : true,
            active_website: existingStock ? (existingStock.active_website || existingStock.active_website === 1 ? true : false) : true,
            active_qr: existingStock ? (existingStock.active_qr || existingStock.active_qr === 1 ? true : false) : true,
            active_kiosk: existingStock ? (existingStock.active_kiosk || existingStock.active_kiosk === 1 ? true : false) : true,
            active_pos: existingStock ? (existingStock.active_pos || existingStock.active_pos === 1 ? true : false) : true,
            _deleted: false,
            _dirty: false,
          })
        })
      })
    },

    async changePage (newPage) {
      if (newPage < 1 || newPage > this.productsPagination.totalPages) return

      await this.autoSave()

      this.productsPagination.page = newPage
      this.loadProducts()
    },

    async autoSave () {
      const payload = this.buildPayload()
      if (!payload.products.length && !payload.products_to_delete.length) return

      return new Promise((resolve) => {
        const data = {
          token: this.token,
          company_id: this.companyId,
          ...payload
        }

        this.apiRequest('/product-stocks/products/stocks', 'POST', data, (response) => {
          if (response.status) {
            payload.products.forEach(p => {
              const key = `${p.product_id}_${p.location_id}`
              if (this.stockData[key]) {
                Vue.set(this.stockData[key], '_dirty', false)
              }
            })
            // Update stock_id for created items from API response
            if (response.saved) {
              response.saved.forEach(savedItem => {
                if (savedItem.action === 'created') {
                  const key = `${savedItem.product_id}_${savedItem.location_id}`
                  if (this.stockData[key]) {
                    Vue.set(this.stockData[key], 'stock_id', savedItem.stock_id)
                    Vue.set(this.stockData[key], '_original_stock_id', savedItem.stock_id)
                  }
                }
              })
            }
            // Clear stock_id for deleted items so re-activation creates new record
            payload.products_to_delete.forEach(p => {
              const key = `${p.product_id}_${p.location_id}`
              if (this.stockData[key]) {
                Vue.set(this.stockData[key], 'stock_id', null)
                Vue.set(this.stockData[key], '_original_stock_id', null)
              }
            })
          }
          resolve(response)
        })
      })
    },

    async saveStocks () {
      // Validare custom: rezervare nu poate depăși stock când allowNegative e false
      let validationErrors = []
      Object.values(this.stockData).forEach(stock => {
        if (stock.active && !stock.allow_negative_stock) {
          const rezervare = parseFloat(stock.rezervare_stock) || 0
          const stoc = parseFloat(stock.stock) || 0
          if (rezervare > stoc) {
            validationErrors.push(`Produs ${stock.product_id} - Locație ${stock.location_id}: Rezervarea depășește stockul`)
          }
        }
      })

      if (validationErrors.length > 0) {
        swal({
          title: '',
          text: validationErrors.join('\n'),
          type: 'error',
          showConfirmButton: false,
          timer: 3000
        })
        return
      }

      const isValid = await this.$validator.validateAll()
      if (!isValid) {
        swal({
          title: '',
          text: this.$t('validations.checkErrors'),
          type: 'error',
          showConfirmButton: false,
          timer: 2000
        })
        return
      }

      this.saving = true

      const payload = this.buildPayload()

      const data = {
        token: this.token,
        company_id: this.companyId,
        ...payload
      }

      this.apiRequest('/product-stocks/products/stocks', 'POST', data, (response) => {
        this.saving = false

        if (response.status) {
          Object.keys(this.stockData).forEach(key => {
            Vue.set(this.stockData[key], '_dirty', false)
          })

          // Update stock_id for created items from API response
          if (response.saved) {
            response.saved.forEach(savedItem => {
              if (savedItem.action === 'created') {
                const key = `${savedItem.product_id}_${savedItem.location_id}`
                if (this.stockData[key]) {
                  Vue.set(this.stockData[key], 'stock_id', savedItem.stock_id)
                  Vue.set(this.stockData[key], '_original_stock_id', savedItem.stock_id)
                }
              }
            })
          }
          // Clear stock_id for deleted items so re-activation creates new record
          payload.products_to_delete.forEach(p => {
            const key = `${p.product_id}_${p.location_id}`
            if (this.stockData[key]) {
              Vue.set(this.stockData[key], 'stock_id', null)
              Vue.set(this.stockData[key], '_original_stock_id', null)
            }
          })

          swal({
            title: '',
            text: this.$t('stock.stocksSavedSuccess'),
            type: 'success',
            showConfirmButton: false,
            timer: 1500
          })
        } else {
          console.log(response.errors)
          if (response.errors) {
            for (let field in response.errors) {
              this.errors.add(field, response.errors[field])
            }
          }

          swal({
            title: '',
            text: response.message || this.$t('validations.wentWrong'),
            type: 'error',
            showConfirmButton: false,
            timer: 2000
          })
        }
      })
    },

    buildPayload () {
      const productsToSave = []
      const productsToDelete = []

      Object.values(this.stockData).forEach(stock => {
        if (stock._deleted && stock._original_stock_id) {
          productsToDelete.push({
            product_id: stock.product_id,
            location_id: stock.location_id,
            stock_id: stock._original_stock_id
          })
          return
        }

        if (stock.active && stock._dirty) {
          productsToSave.push({
            product_id: stock.product_id,
            location_id: stock.location_id,
            stock_id: stock.stock_id,
            stock: parseFloat(stock.stock) || 0,
            rezervare_stock: parseInt(stock.rezervare_stock) || 0,
            sync_gestiune: !!stock.sync_gestiune,
            decrease_stock: !!stock.decrease_stock,
            allow_negative_stock: !!stock.allow_negative_stock,
            active_app: !!stock.active_app,
            active_website: !!stock.active_website,
            active_qr: !!stock.active_qr,
            active_kiosk: !!stock.active_kiosk,
            active_pos: !!stock.active_pos,
          })
        }
      })

      return {
        products: productsToSave,
        products_to_delete: productsToDelete
      }
    },

    goToProduct (prod) {
      return 0;
      this.$router.push({
        name: 'Edit Product',
        params: {
          id: prod.id,
          productData: prod,
          mainCategories: this.mainCategories,
        }
      })
    },

    backButton () {
      this.$bus.$emit('refresh')
      this.$bus.$emit('close-modal')
    }
  }
}
</script>

<style scoped>
/* Card pentru tabel */
.pricing-table-card {
  margin-top: 20px;
  margin-bottom: 100px;
}

.pricing-table-card .card-header {
  padding: 15px 20px;
  border-bottom: 1px solid #ebeef5;
}

.pricing-table-card .card-header h4 {
  margin: 0 0 5px 0;
  font-size: 16px;
  font-weight: 600;
}

/* Filtre */
.filters-wrapper {
  padding: 15px 20px;
  background-color: #f5f7fa;
  border-bottom: 1px solid #ebeef5;
  margin-bottom: 15px;
}

.filters-wrapper .form-group {
  margin-bottom: 0;
}

/* Container scroll */
.table-responsive-wrapper {
  position: relative;
  width: 100%;
  overflow: visible;
}

.table-horizontal-scroll {
  overflow-x: auto;
  overflow-y: visible;
  max-width: 100%;
  border: 1px solid #ebeef5;
  border-radius: 4px;
}

/* Tabel styling */
.pricing-table {
  width: auto;
  min-width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  margin-bottom: 0;
}

.pricing-table th,
.pricing-table td {
  border-right: 1px solid #ebeef5;
  border-bottom: 1px solid #ebeef5;
  vertical-align: top;
}

.pricing-table th:last-child,
.pricing-table td:last-child {
  border-right: none;
}

/* Coloana produs (sticky) */
.product-col {
  min-width: 200px;
  max-width: 220px;
  background-color: #fff;
  z-index: 10;
}

.sticky-col {
  position: sticky;
  left: 0;
  box-shadow: 2px 0 5px rgba(0,0,0,0.1);
  background-color: #fff;
}

.pricing-table thead .product-col {
  background-color: #f5f7fa;
  z-index: 20;
}

.product-info {
  padding: 10px;
}

.product-info strong {
  display: block;
  margin-bottom: 5px;
  color: #303133;
  cursor: pointer;
}

.product-info strong:hover {
  color: #409eff;
  text-decoration: underline;
}

.product-id {
  font-size: 11px;
  margin-bottom: 3px;
}

.standard-price {
  font-size: 12px;
}

/* Coloane locație */
.location-col {
  min-width: 150px;
  max-width: 170px;
  text-align: center;
  padding: 10px 6px !important;
  background-color: #f5f7fa;
  font-size: 12px;
  font-weight: 600;
  color: #409eff;
  white-space: normal;
}

/* Celule locație */
.location-cell {
  min-width: 150px;
  max-width: 170px;
  padding: 8px !important;
  background-color: #fff;
}

.location-cell:hover {
  background-color: #f5f7fa;
}

.location-cell.not-available {
  background-color: #fef0f0;
  opacity: 0.6;
}

.location-cell.inactive {
  background-color: #f4f4f5;
  opacity: 0.7;
}

.not-available-text {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  min-height: 100px;
  color: #f56c6c;
  font-size: 11px;
  gap: 4px;
}

.not-available-text i {
  font-size: 16px;
}

/* Field rows */
.field-row {
  margin-bottom: 6px;
}

.field-row label {
  display: block;
  font-size: 10px;
  color: #909399;
  text-transform: uppercase;
  font-weight: 600;
  margin-bottom: 2px;
  line-height: 1.2;
}

/* Activ / Inactiv */
.active-row {
  margin-bottom: 8px;
  padding-bottom: 6px;
  border-bottom: 2px solid #ebeef5;
}

.active-label {
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  font-size: 12px;
  font-weight: 600;
  color: #67c23a;
  margin: 0;
}

.active-label input[type="checkbox"] {
  margin: 0;
  width: 16px;
  height: 16px;
  cursor: pointer;
}

/* Câmpuri disabled */
.disabled-fields {
  opacity: 0.4;
  pointer-events: none;
  filter: grayscale(0.6);
}

/* Input styling */
.input-sm {
  height: 28px;
  padding: 4px 8px;
  font-size: 12px;
  border-radius: 3px;
  border: 1px solid #dcdfe6;
  width: 100%;
}

.input-sm:focus {
  border-color: #409eff;
  outline: none;
}

.input-sm.disabled-input {
  background-color: #f5f7fa;
  color: #909399;
  cursor: not-allowed;
  border-color: #e4e7ed;
}

.input-error {
  border-color: #f56c6c !important;
}

/* Checkbox rows */
.checkbox-row {
  margin-bottom: 4px;
}

.checkbox-label {
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  font-size: 11px;
  color: #606266;
  margin: 0;
}

.checkbox-label input[type="checkbox"] {
  margin: 0;
  width: 14px;
  height: 14px;
  cursor: pointer;
}

.checkbox-label.disabled-label {
  color: #c0c4cc;
  cursor: not-allowed;
}

.checkbox-label.disabled-label input[type="checkbox"] {
  cursor: not-allowed;
  opacity: 0.5;
}

/* Paginare */
.pagination-wrapper {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 20px;
  border-top: 1px solid #ebeef5;
  background-color: #f5f7fa;
}

.pagination-info {
  font-size: 13px;
  color: #606266;
}

.pagination-controls {
  display: flex;
  gap: 10px;
}

/* Save wrapper */
.save-wrapper {
  padding: 20px;
  text-align: right;
  border-top: 1px solid #ebeef5;
}

.save-wrapper .btn {
  padding: 10px 24px;
  font-size: 14px;
}

/* Loading animation */
.rotate {
  animation: rotate 1s linear infinite;
  display: inline-block;
}

@keyframes rotate {
  from { transform: rotate(360deg); }
  to { transform: rotate(0deg); }
}

/* Row alternativ */
.pricing-table tbody tr:nth-child(even) td:not(.sticky-col) {
  background-color: #fafafa;
}

.pricing-table tbody tr:hover td:not(.sticky-col) {
  background-color: #f5f7fa;
}

/* Responsive */
@media (max-width: 768px) {
  .product-col {
    min-width: 150px;
    max-width: 150px;
  }

  .location-col,
  .location-cell {
    min-width: 130px;
    max-width: 130px;
  }

  .filters-wrapper .col-md-4,
  .filters-wrapper .col-md-3,
  .filters-wrapper .col-md-2 {
    margin-bottom: 10px;
  }
}
</style>


// WEBPACK FOOTER //
// src/components/Dashboard/Views/Products/Stock.vue